import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Input, Button, List, Space, Modal, Form, Select, Spin } from 'antd';
import ReactMarkdown from 'react-markdown';
const { Option } = Select;
const { TextArea } = Input;


const isDev = process.env.NODE_ENV === 'development';
const GameWindow = () => {
    const [background, setBackground] = useState('');
    const [mainStory, setMainStory] = useState('');
    const [character, setCharacter] = useState({});
    const [currentEvent, setCurrentEvent] = useState(null);
    const [backgroundInput, setBackgroundInput] = useState('');
    const [mainStoryInput, setMainStoryInput] = useState('');
    const [characterInput, setCharacterInput] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEventModalVisible, setIsEventModalVisible] = useState(false);
    const [startTime, setStartTime] = useState(null);
    const [model, setModel] = useState(''); // 模型名称
    const [siteValue, setSiteValue] = useState('proxy-chat-ip-1.yy2012.top:11434'); // 地址的值
    const [modelOptions, setModelOptions] = useState([]); // 模型选项
    const [isModelSelected, setIsModelSelected] = useState(false); // 模型是否已选定

    const backgroundRef = useRef(background);
    const mainStoryRef = useRef(mainStory);
    const characterRef = useRef(character);

    const BACKGROUND_PROMPT = `根据以下游戏配置生成完整的背景设定，严格按照JSON格式返回：
{
    "background": "详细的世界观背景描述",
    "main_story": "故事主线概述",
    "character": {
        "health": "初始健康值状态",
        "inventory": ["初始物品1", "初始物品2"],
        "buffs": ["初始增益效果"],
        "debuffs": ["初始减益效果"],
        "skills": ["初始技能1", "初始技能2"]
    }
}

配置要求：
- 世界观：{background}
- 主线情节：{main_story}
- 主角身份：{character}`;

    const EVENT_PROMPT = `根据当前游戏状态背景故事，主线进度，角色状态生成新的事件节点，需要合理的接续主线进度。
当前游戏状态：
- 背景故事：{current_background}
- 主线进度：{main_story_progress}
- 角色状态：{character}

严格按照以下JSON格式返回：
{
    "event": "事件标题",
    "description": "详细事件描述(300字以上)",
    "participants": ["参与者1", "参与者2"],
    "options": [
        {
            "choice": "选项文本1",
            "time_feedback": {
                "early": {
                    "result": "提前选择的结果(100字)",
                    "character": {
                        "health": "更新健康值描述",
                        "inventory": ["更新物品描述列表"],
                        "buffs": ["更新增益效果描述列表"],
                        "debuffs": ["更新减益效果描述列表"],
                        "skills": ["更新技能描述列表"]
                    },
                    "is_end": false
                },
                "on_time": {
                    "result": "准时选择的结果(100字)",
                    "character": {
                        "health": "更新健康值描述",
                        "inventory": ["更新物品描述列表"],
                        "buffs": ["更新增益效果描述列表"],
                        "debuffs": ["更新减益效果描述列表"],
                        "skills": ["更新技能描述列表"]
                    },
                    "is_end": false
                },
                "late": {
                    "result": "延迟选择的结果(100字)",
                    "character": {
                        "health": "更新健康值描述",
                        "inventory": ["更新物品描述列表"],
                        "buffs": ["更新增益效果描述列表"],
                        "debuffs": ["更新减益效果描述列表"],
                        "skills": ["更新技能描述列表"]
                    },
                    "is_end": false
                }
            }
        }
        // 至少3个选项...
    ]
}
    `;

    const fetchModels = useCallback(async () => {
        try {
            let response = null;
            if (isDev) {
                response = await fetch(`http://${siteValue}/api/tags`, {
                    method: 'GET',
                });
                const data = await response.json();
                setModelOptions(data.models.map(model => model.model));
                if (data.models.length > 0) {
                    setModel(data.models[0].model);
                }
            } else {
                response = await fetch(`https://proxy-chat-proxy-1.yy2012.top`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        "url": siteValue + "/api/tags",
                        "method": "GET",
                        "json": {}
                    }),
                });
                const data = await response.json();
                setModelOptions(data.models.map(model => model.model));
                if (data.models.length > 0) {
                    setModel(data.models[0].model);
                }
            }
        } catch (error) {
            console.error('Failed to fetch models:', error);
        }
    }, [siteValue]);

    useEffect(() => {
        fetchModels();
    }, [fetchModels]);

    const fetchBackground = async () => {
        const prompt = BACKGROUND_PROMPT.replace('{background}', backgroundInput)
            .replace('{main_story}', mainStoryInput)
            .replace('{character}', characterInput);

        let response = null;
        if (isDev) {
            response = await fetch(`http://${siteValue}/api/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stream: false, model: model, prompt }),
            });
        } else {
            response = await fetch(`https://proxy-chat-proxy-1.yy2012.top`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    "url": siteValue + "/api/generate",
                    "method": "POST",
                    "json": { stream: false, model: model, prompt }
                }),
            });
        }
        const data = await response.json();
        const jsonStr = findLastValidJSON(data.response);
        const parsedData = JSON.parse(jsonStr);
        setBackground(parsedData.background);
        setMainStory(parsedData.main_story);
        setCharacter(parsedData.character);

        backgroundRef.current = parsedData.background;

        mainStoryRef.current = parsedData.main_story;

        characterRef.current = parsedData.character;
    };

    function findLastValidJSON(str) {
        // 收集所有右大括号的位置
        const closingBraceIndices = [];
        for (let i = 0; i < str.length; i++) {
          if (str[i] === '}') closingBraceIndices.push(i);
        }
      
        // 从后向前检查每个右括号
        for (let i = closingBraceIndices.length - 1; i >= 0; i--) {
          const end = closingBraceIndices[i] + 1; // 包含右括号
          const start = findMatchingBrace(str, closingBraceIndices[i]);
      
          if (start === -1) continue;
      
          const candidate = str.slice(start, end);
          try {
            const json = JSON.parse(candidate);
            if (typeof json === 'object' && json !== null) {
              return JSON.stringify(json, null, 2); // 返回格式化后的JSON
            }
          } catch (e) {
            continue; // 无效则继续
          }
        }
        return null;
      }
      
      // 寻找匹配的左大括号位置
      function findMatchingBrace(str, endIndex) {
        let count = 1;
        for (let i = endIndex - 1; i >= 0; i--) {
          if (str[i] === '}') count++;
          else if (str[i] === '{') {
            count--;
            if (count === 0) return i;
          }
        }
        return -1;
      }

    const fetchEvent = async () => {
        const prompt = EVENT_PROMPT.replace('{current_background}', backgroundRef.current)
            .replace('{main_story_progress}', mainStoryRef.current)
            .replace('{character}', JSON.stringify(characterRef.current));
        // console.log(prompt);
        let response = null;
        try {
            if (isDev) {
                response = await fetch(`http://${siteValue}/api/generate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ stream: false, model: model, prompt }),
                });
            } else {
                response = await fetch(`https://proxy-chat-proxy-1.yy2012.top`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        "url": siteValue + "/api/generate",
                        "method": "POST",
                        "json": { stream: false, model: model, prompt }
                    }),
                });
            }
            const data = await response.json();
            const jsonStr = findLastValidJSON(data.response);
            const parsedData = JSON.parse(jsonStr);
            if(parsedData.options.length < 3){
                throw Error('Failed to fetch event: options less than 3');
            }
            parsedData.options.forEach(element => {
                if("early" in element.time_feedback && "on_time" in element.time_feedback && "late" in element.time_feedback){
                    //pass
                }else{
                    throw Error('Failed to fetch event: time_feedback not complete');
                }
            });

            setCurrentEvent(parsedData);
            setMainStory(mainStoryRef.current + '\n' + parsedData.description);
            mainStoryRef.current = mainStoryRef.current + '\n' + parsedData.description;
            setIsEventModalVisible(true);
        } catch (error) {
            console.error('Failed to fetch event:', error);
            await fetchEvent(); // 重新发送请求
        }
    };

    const handleEventChoice = async (options) => {
        setIsSending(true);
        const elapsedTime = (Date.now() - startTime) / 1000;
        let timing = 'late';
        if (elapsedTime <= 3) {
            timing = 'early';
        } else if (elapsedTime <= 6) {
            timing = 'on_time';
        }
        setMainStory(mainStoryRef.current + options.time_feedback[timing].result);
        mainStoryRef.current = mainStoryRef.current + options.time_feedback[timing].result;
        setCharacter(options.time_feedback[timing].character);
        characterRef.current = options.time_feedback[timing].character;

        await fetchEvent();
        setIsSending(false);
    };

    const handleModelConfirm = () => {
        setIsModelSelected(true);
        setIsModalVisible(true);
    };

    const handleModalOk = async () => {
        setIsModalVisible(false);
        setIsSending(true);
        await fetchBackground();
        await fetchEvent();
        setIsSending(false);
    };

    const handleEventModalOk = () => {
        setIsEventModalVisible(false);
        setStartTime(Date.now());
    };

    return (
        <div style={{ padding: '5vh', maxWidth: '100vw', boxSizing: 'border-box' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Space.Compact style={{ width: '100%' }}>
                    <Input value={siteValue} onChange={e => setSiteValue(e.target.value)} addonBefore="http://" addonAfter="/api/chat" style={{ flex: 1 }} />
                    <Button type="primary" onClick={fetchModels}>确认</Button>
                </Space.Compact>
                <Form.Item required>
                    <Select
                        value={model}
                        onChange={(value) => {
                            setModel(value);
                        }}
                        style={{ width: '100%' }}
                        placeholder="Select a model"
                        allowClear
                        disabled={isModelSelected}
                    >
                        {modelOptions.map(option => (
                            <Option key={option} value={option}>
                                {option}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Button type="primary" onClick={handleModelConfirm} disabled={!model || isModelSelected}>
                    确认
                </Button>
                <Card title="历史背景故事" style={{ width: '100%', margin: '0 auto' }}>
                    <ReactMarkdown>{background}</ReactMarkdown>
                </Card>
                <Card title="主线故事" style={{ width: '100%', margin: '0 auto' }}>
                    <ReactMarkdown>{mainStory}</ReactMarkdown>
                </Card>
                <Card title="角色状态" style={{ width: '100%', margin: '0 auto' }}>
                    <pre>{JSON.stringify(character, null, 2)}</pre>
                </Card>
                <Card title="当前事件" style={{ width: '100%', margin: '0 auto' }}>
                    {isSending ? (
                        <Spin tip="加载中...">
                            <div style={{ height: '60vh' }} />
                        </Spin>
                    ) : (
                        currentEvent && (
                            <div>
                                <ReactMarkdown>{currentEvent.description}</ReactMarkdown>
                                <List
                                    dataSource={currentEvent.options}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <Button onClick={() => handleEventChoice(item)} disabled={isSending}>
                                                {item.choice}
                                            </Button>
                                        </List.Item>
                                    )}
                                />
                            </div>
                        )
                    )}
                </Card>
            </Space>
            <Modal title="输入背景故事" open={isModalVisible} onOk={handleModalOk} footer={null}>
                <Form layout="vertical">
                    <Form.Item label="背景故事">
                        <TextArea
                            value={backgroundInput}
                            onChange={(e) => setBackgroundInput(e.target.value)}
                            placeholder="输入背景故事..."
                            rows={4}
                        />
                    </Form.Item>
                    <Form.Item label="主线故事">
                        <TextArea
                            value={mainStoryInput}
                            onChange={(e) => setMainStoryInput(e.target.value)}
                            placeholder="输入主线故事..."
                            rows={4}
                        />
                    </Form.Item>
                    <Form.Item label="角色信息">
                        <TextArea
                            value={characterInput}
                            onChange={(e) => setCharacterInput(e.target.value)}
                            placeholder="输入角色信息..."
                            rows={4}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" onClick={handleModalOk}>
                            确认
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
            <Modal title="事件生成完成" open={isEventModalVisible} onOk={handleEventModalOk} footer={null}>
                <p>事件生成完成，请点击确认开始选择。</p>
                <Button type="primary" onClick={handleEventModalOk}>
                    确认
                </Button>
            </Modal>
        </div>
    );
};

export default GameWindow;
